<header class="editor-header">
  <span class="wordmark">FLIPPY</span><span class="divider"></span>
  <a routerLink="/dashboard" class="breadcrumb">Boards</a><span class="separator">/</span>
  <h1>{{ board()?.name || 'Board editor' }}</h1>
  <span class="spacer"></span>
  <button
    class="save-status"
    [class.failed]="session.status() === 'error'"
    [class.saved]="session.status() === 'saved'"
    [disabled]="session.status() !== 'error'"
    (click)="retry()"
    aria-live="polite"
  >
    <i></i
    >{{
      session.status() === 'saved'
        ? 'Saved'
        : session.status() === 'error'
          ? 'Not saved — retry'
          : session.status() === 'loading'
            ? 'Loading…'
            : 'Saving…'
    }}
  </button>
  @if (board()?.public_id) {
    <a class="live-link" [href]="'/display/' + board()!.public_id" target="_blank" rel="noopener"
      >Live view ↗</a
    >
  }
  <a class="done" routerLink="/dashboard">Done</a>
</header>
@if (board(); as board) {
  <main class="workspace">
    <section class="editor-column" aria-label="Message editor">
      <div
        class="preview-frame"
        role="img"
        [attr.aria-label]="'Message preview: ' + (selected()?.text || 'Empty board')"
      >
        <div
          class="preview-grid"
          [style.--columns]="board.config.columns"
          [style.max-width.px]="board.config.columns * 34 - 4"
          [style.grid-template-columns]="'repeat(' + board.config.columns + ', minmax(0, 1fr))'"
        >
          @for (char of grid(); track $index) {
            <span
              class="tile"
              [style.background]="
                $index < board.config.columns && char !== ' ' && selected()?.accentColor
                  ? selected()!.accentColor
                  : null
              "
              [style.color]="
                $index < board.config.columns && char !== ' ' && selected()?.accentColor
                  ? ink(selected()!.accentColor!)
                  : null
              "
              >{{ char === ' ' ? '\u00a0' : char }}</span
            >
          }
        </div>
      </div>
      @if (selected(); as message) {
        <label class="sr-only" for="message-text">Message text</label>
        <textarea
          #messageInput
          id="message-text"
          [rows]="board.config.rows"
          [ngModel]="message.text"
          (ngModelChange)="textChange($event)"
          spellcheck="false"
          placeholder="Type your message…"
        ></textarea>
        <div class="message-controls">
          <div class="control-pill">
            <span>Align</span>
            <div class="segments" role="group" aria-label="Message alignment">
              @for (align of alignments; track align; let i = $index) {
                <button
                  [class.active]="(message.align || 'left') === align"
                  [attr.aria-pressed]="(message.align || 'left') === align"
                  [attr.aria-label]="'Align ' + align"
                  (click)="setAlign(align)"
                >
                  {{ ['⟵', '⟷', '⟶'][i] }}
                </button>
              }
            </div>
          </div>
          <div class="control-pill swatches">
            <span>Line 1 tiles</span>
            <div role="group" aria-label="First line tile color">
              @for (color of colors; track color; let i = $index) {
                <button
                  [style.background]="color"
                  [class.selected]="(message.accentColor || colors[0]) === color"
                  [attr.aria-pressed]="(message.accentColor || colors[0]) === color"
                  [attr.aria-label]="
                    ['No highlight', 'Orange', 'Yellow', 'Green', 'Deep orange', 'Cream'][i]
                  "
                  (click)="setColor(color)"
                ></button>
              }
            </div>
          </div>
          <div class="expiry-control">
            <button
              class="expiry-chip"
              [attr.aria-expanded]="expiryOpen()"
              (click)="expiryOpen.set(!expiryOpen())"
            >
              expires {{ expiryLabel(message) }} ▾
            </button>
            @if (expiryOpen()) {
              <div class="expiry-popover">
                <label
                  >Expiration (local time)<input
                    type="datetime-local"
                    [ngModel]="expiryValue()"
                    (ngModelChange)="setExpiry($event)" /></label
                ><button (click)="setExpiry(''); expiryOpen.set(false)">Never expires</button>
              </div>
            }
          </div>
          <span class="fit-note" [class.overflow]="overflow()">{{
            overflow()
              ? 'Text too long for ' +
                board.config.rows +
                ' rows × ' +
                board.config.columns +
                ' columns'
              : 'Fits ' + board.config.columns + ' columns'
          }}</span>
        </div>
        <p class="editing-note">
          Editing "{{ messageName(message) }}" · posts as you type · ⌘Z to undo
        </p>
      } @else {
        <div class="empty-state">
          <p>No messages yet. Add your first message to start the rotation.</p>
          <button (click)="addMessage()">+ New message</button>
        </div>
      }
      @if (session.error() || settingError()) {
        <p role="alert" class="error">{{ settingError() || session.error() }}</p>
      }
    </section>
    <aside class="rail" aria-label="Rotation and board settings">
      <div class="section-heading">
        <h2>ROTATION</h2>
        <span
          >{{ board.messages.length }} ·
          {{ board.messages.length * (board.config.rotationInterval || 10) }}s loop</span
        >
      </div>
      <div class="rotation-list">
        @for (message of board.messages; track message.id) {
          <div
            class="message-row"
            [class.showing]="nowShowing() === message.id"
            [class.selected-row]="session.selectedId() === message.id"
            (dragover)="$event.preventDefault()"
            (drop)="drop(message.id, $event)"
          >
            <button
              class="drag-handle"
              draggable="true"
              (dragstart)="dragStart(message.id, $event)"
              (dragend)="draggedId = null"
              (keydown)="reorderKey(message.id, $event)"
              [attr.aria-label]="'Reorder ' + messageName(message) + ': use up and down arrows'"
            >
              ⠿
            </button>
            <button
              class="message-select"
              [attr.aria-pressed]="session.selectedId() === message.id"
              (click)="select(message.id)"
            >
              <span class="queue-text">{{ queueText(message) }}</span
              ><span class="message-meta">{{ meta(message) }}</span>
            </button>
            <div class="row-actions">
              <button
                (click)="duplicate(message.id)"
                [attr.aria-label]="'Duplicate ' + messageName(message)"
                title="Duplicate"
              >
                ⧉</button
              ><button
                class="delete"
                (click)="remove(message.id)"
                [attr.aria-label]="'Delete ' + messageName(message)"
                title="Delete"
              >
                ×
              </button>
            </div>
          </div>
        }
      </div>
      <button class="new-message" (click)="addMessage()">+ New message</button>
      <section class="board-settings">
        <div class="section-heading"><h2>BOARD SETTINGS</h2></div>
        <label class="setting-row"
          ><span>Name</span
          ><input
            class="text-setting"
            aria-label="Board name"
            [ngModel]="board.name"
            (ngModelChange)="setName($event)"
            maxlength="255"
        /></label>
        <div class="setting-row">
          <span>Size</span>
          <div class="size-inputs">
            <input
              type="number"
              aria-label="Board rows"
              min="2"
              max="10"
              [value]="board.config.rows"
              (change)="resize('rows', $event)"
            /><span>×</span
            ><input
              type="number"
              aria-label="Board columns"
              min="10"
              max="60"
              [value]="board.config.columns"
              (change)="resize('columns', $event)"
            />
          </div>
        </div>
        <label class="setting-row"
          ><span>Rotation</span
          ><span
            ><input
              class="rotation-input"
              type="number"
              aria-label="Rotation seconds"
              min="5"
              max="60"
              [value]="board.config.rotationInterval"
              (change)="setRotation($event)"
            />
            s</span
          ></label
        >
        <div class="setting-row">
          <span>Flip sound</span
          ><button
            class="sound-toggle"
            role="switch"
            aria-label="Flip sound"
            [attr.aria-checked]="board.soundEnabled !== false"
            [class.on]="board.soundEnabled !== false"
            (click)="toggleSound()"
          >
            <span></span>
          </button>
        </div>
        <div class="setting-row">
          <span>Passcode</span
          ><button
            class="setting-chip"
            [attr.aria-expanded]="passcodeOpen()"
            (click)="passcodeOpen.set(!passcodeOpen())"
          >
            {{ board.hasPasscode || board.passcode ? '••••' : 'Set passcode' }}
          </button>
        </div>
        @if (passcodeOpen()) {
          <label class="passcode-edit"
            >Required for public display access. Passcode changes cannot be undone.<input
              type="password"
              aria-label="Board passcode"
              autocomplete="new-password"
              minlength="4"
              maxlength="50"
              [placeholder]="board.hasPasscode ? 'Keep current passcode' : 'At least 4 characters'"
              [ngModel]="board.passcode === '***' ? '' : board.passcode"
              (ngModelChange)="setPasscode($event)"
          /></label>
        }
        <label class="setting-row"
          ><span>Public title</span
          ><input
            class="text-setting"
            aria-label="Public title"
            [ngModel]="board.title"
            (ngModelChange)="setTitle($event)"
            maxlength="255"
            placeholder="FlippyBoards.com"
        /></label>
        <p class="settings-footnote">
          Everything here saves on change — no Save button, and Done just navigates back.
        </p>
      </section>
    </aside>
  </main>
} @else {
  <div class="loading" role="status">{{ session.error() || 'Loading your board…' }}</div>
}
